import {consoleLogger} from '@gongxigames/framework/core/fragments/ConsoleLogger';

export interface ReturnData {
    text: string;
    json: any;
    arraybuffer: ArrayBuffer;
    blob: Blob;
    document: Document;
}

export type FileLoaderCallBack = (xhr: XMLHttpRequest, ev: ProgressEvent<XMLHttpRequestEventTarget>) => any;

export type FileLoaderOptions<T extends keyof ReturnData> = {
    url: string;
    done?: (result: ReturnData[T]) => void;
    load?: FileLoaderCallBack;
    progress?: FileLoaderCallBack;
    error?: FileLoaderCallBack;
    timeout?: FileLoaderCallBack;
    responseType?: T;
    method?: 'GET' | 'POST' | 'DELETE' | 'PUT';
    headers?: [string, string][];
    connectionTimeout?: number;
};

export class FileLoader<T extends keyof ReturnData> {
    private size = 0;
    private progress = 0;
    private loaded = 0;
    private compiled = false;
    private xhr = new XMLHttpRequest();

    constructor(
        public readonly options: FileLoaderOptions<T>
    ) {}

    public load(): Promise<ReturnData[T]> {
        const {options, xhr} = this;
        const {method, url, responseType, headers} = options;

        xhr.responseType = responseType || '';

        headers?.forEach(([key, val]) => xhr.setRequestHeader(key, val));

        if (options.connectionTimeout !== undefined) {
            xhr.timeout = options.connectionTimeout;
        }

        (['load', 'progress', 'error', 'timeout'] as const)
            .forEach(callback => {
                xhr.addEventListener(callback, event => options[callback]?.(xhr, event));
            });

        xhr.addEventListener('progress', event => {
            this.progress = event.loaded / event.total;
            this.size = event.total;
            this.loaded = event.loaded;
        });

        xhr.open(method || 'GET', url, true);
        xhr.send();

        return new Promise((res, rej) => {
            xhr.addEventListener('load', event => {

                try {
                    if (xhr.readyState === 4 && xhr.status >= 400 && xhr.status <= 599) {
                        consoleLogger.warn(`Get request error. Status ${xhr.status}`);

                        if (options.error) {
                            options.error(xhr, event);
                            rej();
                        }
                    } else {
                        const {response} = xhr;

                        this.compiled = true;

                        if (options.done) {
                            options.done(response);
                        }
                        res(response);
                    }
                } catch (e) {
                    if (options.error) {
                        options.error(xhr, event);
                    }
                    consoleLogger.error(e);
                    rej();
                }
            });
        });
    }

    public getSize(): number {
        return this.size;
    }

    public getProgress(): number {
        return this.progress;
    }

    public getLoaded(): number {
        return this.loaded;
    }

    public isCompiled(): boolean {
        return this.compiled;
    }
}
